import React, { useContext } from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import Lozenge from '@atlaskit/lozenge';
import { Text } from '@atlaskit/primitives';

import { RelativeDate } from 'src/components/date';
import { FabricUserAvatar } from 'src/containers/fabric-user-avatar';
import {
  getPullRequestDestinationHash,
  getPullRequestSourceHash,
} from 'src/redux/pull-request/selectors';
import { useHistory__UNSAFE, useParams } from 'src/router/hooks';
import urls from 'src/urls';

import {
  DiffStateContext,
  SelectedDiffType,
  defaultMultiCommitSelection,
} from '../tabs/diff/diff-state-context';

import messages from './diff-comment-navigator.i18n';
import * as styles from './styles';
import { NavComment } from './to-nav-comment';

export function CommentPreview(props: {
  comment: NavComment;
  closeCommentNav: () => void;
  publishUiEventForCommentSelection: (commentId: number) => void;
}): JSX.Element {
  const { setIterativeReviewSelectedDiff, setMultiCommitSelection } =
    useContext(DiffStateContext);
  const prAnchorHash = useSelector(getPullRequestSourceHash) || '';
  const prDestinationHash = useSelector(getPullRequestDestinationHash) || '';
  const { comment, closeCommentNav, publishUiEventForCommentSelection } = props;

  const history = useHistory__UNSAFE();
  const { repositoryOwner, repositorySlug, pullRequestId } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
    pullRequestId: string;
  }>();
  const diffTabUrl = urls.ui.pullRequestTab(
    repositoryOwner,
    repositorySlug,
    pullRequestId,
    'diff'
  );

  const permalink = `comment-thread-${comment.id}`;
  const handleClick = () => {
    const destinationHash = comment.baseRevHash ?? comment.destHash;
    const useAllChangesView = !comment.baseRevHash;
    if (!comment.outdated) {
      setIterativeReviewSelectedDiff({
        sourceHash: useAllChangesView ? prAnchorHash : comment.srcHash!,
        destHash: useAllChangesView ? prDestinationHash : destinationHash,
        selectedDiffType: useAllChangesView
          ? SelectedDiffType.All
          : SelectedDiffType.Custom,
        isLoading: false,
      });
      setMultiCommitSelection(defaultMultiCommitSelection);
    }

    // We need to set the hash first so that hashchange gets triggered so that scrolling works.
    window.location.hash = `#${permalink}`;

    // Then we can set the rest of the path to point to the right place
    if (destinationHash && !useAllChangesView && !comment.outdated) {
      history.replace(
        `${diffTabUrl}/${comment.srcHash}..${comment.destHash}#${permalink}`
      );
    } else {
      history.replace(`${diffTabUrl}#${permalink}`);
    }
    closeCommentNav();
    publishUiEventForCommentSelection(comment.id);
  };

  // for keyboard accessibility of comment previews
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      handleClick();
      e.preventDefault();
    }
  };

  function PreviewText(): JSX.Element {
    if (comment.content) {
      return <Text maxLines={1}>{comment.content}</Text>;
    } else {
      return (
        <styles.NoPreviewAvailable>
          No preview available.
        </styles.NoPreviewAvailable>
      );
    }
  }

  return (
    <styles.CommentPreview
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <CommentPreviewHeader comment={comment} />
      <PreviewText />
    </styles.CommentPreview>
  );
}

function CommentPreviewHeader(props: { comment: NavComment }): JSX.Element {
  const { comment } = props;

  return (
    <styles.CommentPreviewHeader>
      <styles.MetadataAndAvatar>
        <FabricUserAvatar
          size="medium"
          user={comment.createdBy}
          testId="comment-preview-avatar"
        />
        <CommentMetadata comment={comment} />
      </styles.MetadataAndAvatar>
    </styles.CommentPreviewHeader>
  );
}

function CommentMetadata(props: { comment: NavComment }) {
  const { comment } = props;
  const date = <RelativeDate date={new Date(comment.createdAt)} />;
  const filename = comment.file?.split('/').pop();

  return (
    <styles.CommentMetadata>
      <styles.UsernameAndOutdatedLozenge commentIsOutdated={!!comment.outdated}>
        <Text weight="medium">{comment.createdBy.name}</Text>
        {comment.outdated && (
          <Lozenge>
            <FormattedMessage {...messages.outdatedLabel} />
          </Lozenge>
        )}
      </styles.UsernameAndOutdatedLozenge>
      <styles.FileDateDescription>
        <FormattedMessage
          {...messages.commentDescription}
          values={{ date, filename }}
        />
      </styles.FileDateDescription>
    </styles.CommentMetadata>
  );
}
